import { useEffect, useState } from "react";
import { useTenant } from "../../../Contexts/TenantContext";
import Footer from "../../../Components/Footer/Footer";
import SmartNavbar from "../../../Components/SmartNavbar/SmartNavbar";
import PublicPageTitle from "../../../Components/PublicPageTitle/PublicPageTitle";

const UserCompanyPage = () => {
  const { tenantAbout, fetchTenantAbout } = useTenant();
  
  const [loadingAbout, setLoadingAbout] = useState(!tenantAbout?.id);

  useEffect(() => {
    const load = async () => {
      if (!tenantAbout?.id) {
        await fetchTenantAbout();
      }
      setLoadingAbout(false);
    };

    load();
  }, [tenantAbout?.id, fetchTenantAbout]);

  const SkeletonBlock = ({ height = 16, width = "100%" }: { height?: number | string; width?: number | string }) => (
    <div className="animate-pulse bg-gray-200 rounded w-full" style={{ height, maxWidth: width }} />
  );

  const renderSkeleton = () => (
    <div>
      <section className="grid place-items-center text-center page-gutter pt-16 md:pt-[7.5rem] pb-8 sm:pb-16">
        <div className="space-y-4 w-full max-w-3xl">
          <SkeletonBlock height={28} width={240} />
          <SkeletonBlock height={18} width={320} />
          <SkeletonBlock height={18} width={260} />
        </div>
      </section>

      <section className="grid place-items-center page-gutter lg:px-36">
        <SkeletonBlock height={320} />
      </section>

      <section className="grid place-items-center page-gutter py-8 sm:py-16">
        <div className="max-w-6xl space-y-4 w-full">
          {Array.from({ length: 3 }).map((_, index) => (
            <SkeletonBlock key={index} height={80} />
          ))}
        </div>
      </section>

      <section className="grid place-items-center page-gutter lg:px-36">
        <SkeletonBlock height={320} />
      </section>

      <section className="max-w-6xl mx-auto px-4 py-8 space-y-8 mb-8 sm:py-12 sm:mb-24 sm:space-y-10 sm:px-0">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="space-y-3">
            <SkeletonBlock height={24} width={160} />
            <SkeletonBlock height={18} />
            <SkeletonBlock height={18} />
          </div>
        ))}

        <div className="space-y-3">
          <SkeletonBlock height={24} width={220} />
          {Array.from({ length: 4 }).map((_, index) => (
            <SkeletonBlock key={index} height={16} width={360} />
          ))}
        </div>
      </section>
    </div>
  );

  return (
    <div>
      <SmartNavbar />

      {loadingAbout ? (
        renderSkeleton()
      ) : (
        <>
          <section className={`grid place-items-center text-center page-gutter pt-16 md:pt-[7.5rem] pb-8 sm:pb-16`}>
            <div className="max-w-5xl">
              <PublicPageTitle className="mb-2">{"SOBRE A EMPRESA"}</PublicPageTitle>
              <p className={`mx-auto text-sm sm:text-base font-medium`}>
                {tenantAbout?.subtitle}
              </p>
            </div>
          </section>

          {tenantAbout?.aboutImageOne && (
            <section className={`grid place-items-center page-gutter lg:px-36`}>
              <img 
                src={tenantAbout.aboutImageOne.url}
                alt={"Imagem 1 sobre a empresa"}
                className={`max-w-full h-auto object-cover max-h-[420px] rounded-lg shadow-md`}
              />
            </section>
          )}     

          <section className={`grid place-items-center page-gutter py-8 sm:py-16`}>
            <div className={`md:text-justify space-y-6 leading-relaxed max-w-5xl text-sm sm:text-lg`}>
              {tenantAbout?.descriptionOne &&
                <p>{tenantAbout.descriptionOne}</p>
              }

              {tenantAbout?.descriptionTwo &&
                <p>{tenantAbout.descriptionTwo}</p>
              }

              {tenantAbout?.descriptionThree &&
                <p>{tenantAbout.descriptionThree}</p>
              }
            </div>
          </section>
          
          {tenantAbout?.aboutImageTwo && (
            <section className={`grid place-items-center page-gutter lg:px-36`}>
              <img 
                src={tenantAbout.aboutImageTwo.url}
                alt={"Imagem 2 sobre a empresa"}
                className={`max-w-full h-auto object-cover max-h-[420px] rounded-lg shadow-md`}
              />
            </section>
          )}

          <section className={`md:text-justify mx-auto page-gutter py-8 max-w-[72rem] space-y-10 mb-8 sm:py-12 sm:mb-24 sm:space-y-20`}>
            <div>
              {tenantAbout?.business &&
                <>
                  <h2 className={`font-semibold mb-2 text-base sm:text-2xl text-primary-600`}>{"NEGÓCIO"}</h2>
                  <p className={`leading-relaxed text-sm sm:text-lg`}>{tenantAbout.business}</p>
                </>
              }
            </div>

            <div>
              {tenantAbout?.mission &&
                <>
                  <h2 className={`font-semibold mb-2 text-base sm:text-2xl text-primary-600`}>{"MISSÃO"}</h2>
                  <p className={`leading-relaxed text-sm sm:text-lg`}>{tenantAbout.mission}</p> 
                </>
              }
            </div>

            <div>
              {tenantAbout?.vision &&
                <>
                  <h2 className={`font-semibold mb-2 text-base sm:text-2xl text-primary-600`}>{"VISÃO"}</h2>
                  <p className={`leading-relaxed text-sm sm:text-lg`}>{tenantAbout.vision}</p>
                </>
              }        
            </div>
            
            <div>
              {tenantAbout?.values && (
                <>
                  <h2 className={`font-semibold mb-4 text-base sm:text-2xl text-primary-600`}>
                    {"VALORES E PRINCÍPIOS"}
                  </h2>
                  <ul className={`space-y-2 text-sm sm:text-lg`}>
                    {tenantAbout.values
                      .split('\n')
                      .map(line => line.trim())
                      .filter(line => line.includes(':'))
                      .map((line, index) => {
                        const [title, ...rest] = line.split(':');
                        const description = rest.join(':').trim();
                        return (
                          <li key={index}>
                            <strong>{title}:</strong> {description}
                          </li>
                        );
                      })
                    }
                  </ul>
                </>
              )}
            </div>
          </section>
        </>
      )}
      <Footer />
    </div>
  );
};

export default UserCompanyPage;
